//Libs
import XMarkIcon from "@heroicons/react/24/solid/XMarkIcon";
import { z } from "zod";
import { useEffect, useMemo, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

//Local
import BaseModal from "../BaseModal";
import * as strings from "../../strings";
import { ExistingVehicle } from "../../models/vehicle";
import { getDefaultLocationID } from "../estimates/AddEditPriceBookItemDialog";
import { ExistingSiteKeyLocation } from "../../models/site-key-location";
import { logger as devLogger } from "../../logging";
import { ExistingSiteKeyUserPermissions } from "../../models/site-key-user-permissions";
import { ErrorMessage } from "../ErrorMessage";
import BaseButtonSecondary from "../BaseButtonSecondary";
import BaseButtonPrimary from "../BaseButtonPrimary";
import BaseInputSelect from "../BaseInputSelect";
import StyledMessage from "../StyledMessage";
import BaseInputText from "../BaseInputText";
import BaseInputTextArea from "../BaseInputTextArea";
import { IMultipleUid_AssignUser } from "../CustomFields/MultipleUidDialog";
import BaseInputCheckbox from "../BaseInputCheckbox";
import { getCheckedAssignedToValues } from "../scheduling/EditStiltCalendarEventDialog";
import { ExistingSiteKey } from "../../models/site-key";
import {
  alphabetizeWorkTypes,
  CraftTypes,
  getReadableCraftType,
  getSortedWorkTypesList,
  isValidCraftType,
} from "../../models/craft-types";
import {
  alphabetizeTaskTypes,
  getReadableTaskType,
  getSortedTaskTypesList,
  isValidTaskType,
  TaskTypes,
} from "../../models/task-types";
import ButtonColored from "../ButtonColored";
import BaseInputNumber from "../BaseInputNumber";
import { StyledTooltip } from "../StyledTooltip";
import HeadingOne from "../HeadingOne";

interface Props {
  /* DATA */
  isDialogOpen: boolean;
  vehicleDoc: ExistingVehicle | null;
  siteKeyLocationList: ExistingSiteKeyLocation[];
  userPermissions: ExistingSiteKeyUserPermissions;
  userList: IMultipleUid_AssignUser[];
  siteKeyDoc: ExistingSiteKey;
  /* FUNCTIONS */
  closeDialog: () => void;
  handleSaveNewVehicle: (newVehicle: VehicleFormState) => Promise<void>;
  handleEditVehicle: (updateVehicle: VehicleFormState) => Promise<void>;
  handleDeleteVehicle: (vehicleID: ExistingVehicle["id"]) => Promise<void>;
}

const VehicleFormSchema = z.object({
  title: z.string().min(1).max(200),
  description: z.string().max(500).nullable(),
  locationID: z.string().min(1).max(200),
  defaultSiteKeyUserID: z.array(z.string()),
  craftTypes: z.array(z.nativeEnum(CraftTypes)),
  taskTypes: z.array(z.nativeEnum(TaskTypes)),
  maxTasksPerDay: z.number().int().min(0).max(100).optional(),
  groupName: z.string().optional(),
  startTime: z
    .string()
    .regex(/^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/)
    .optional(),
  endTime: z
    .string()
    .regex(/^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/)
    .optional(),
  omitFromOptimization: z.boolean().optional(),
});

export type VehicleFormState = z.infer<typeof VehicleFormSchema>;

export default function AddEditVehicleDialog({
  closeDialog,
  handleSaveNewVehicle,
  handleEditVehicle,
  handleDeleteVehicle,
  siteKeyDoc,
  isDialogOpen,
  vehicleDoc,
  siteKeyLocationList,
  userPermissions,
  userList,
}: Props) {
  const [displayError, setDisplayError] = useState<boolean>(false);

  /** Work types available for the current siteKey */
  const validCraftTypes = alphabetizeWorkTypes(
    siteKeyDoc.validCraftTypes.filter(isValidCraftType),
  );

  /** Task types available for the current siteKey */
  const validTaskTypes = alphabetizeTaskTypes(
    siteKeyDoc.validTaskTypes.filter(isValidTaskType),
  );

  const VehicleFormDefaultValues: VehicleFormState = useMemo(() => {
    if (vehicleDoc) {
      const originallyCheckedAssignedTo = getCheckedAssignedToValues(
        vehicleDoc?.defaultSiteKeyUserID,
        userList,
      );

      // Populate the workType values after they've been retrieved from the db.
      const sortedWorkTypesList = getSortedWorkTypesList(
        vehicleDoc.craftTypes,
        siteKeyDoc.validCraftTypes,
      );

      // Populate the workType values after they've been retrieved from the db.
      const sortedTaskTypesList = getSortedTaskTypesList(
        vehicleDoc.taskTypes,
        siteKeyDoc.validTaskTypes,
      );

      return {
        title: vehicleDoc.title,
        description: vehicleDoc.description,
        locationID: vehicleDoc.locationID,
        defaultSiteKeyUserID: originallyCheckedAssignedTo ?? [],
        craftTypes: sortedWorkTypesList ?? [],
        taskTypes: sortedTaskTypesList ?? [],
        maxTasksPerDay: vehicleDoc.maxTasksPerDay,
        groupName: vehicleDoc.groupName,
        startTime: vehicleDoc.startTime,
        endTime: vehicleDoc.endTime,
        omitFromOptimization: vehicleDoc.omitFromOptimization,
      };
    } else {
      return {
        title: "",
        description: "",
        locationID:
          getDefaultLocationID(userPermissions, siteKeyLocationList) ?? "",
        defaultSiteKeyUserID: [],
        craftTypes: [],
        taskTypes: [],
        maxTasksPerDay: undefined,
        groupName: undefined,
        startTime: undefined,
        endTime: undefined,
        omitFromOptimization: false,
      };
    }
  }, [vehicleDoc, userPermissions, siteKeyLocationList, siteKeyDoc, userList]);

  const {
    control,
    formState: { errors, isSubmitting },
    reset,
    handleSubmit,
  } = useForm<VehicleFormState>({
    defaultValues: VehicleFormDefaultValues,
    resolver: zodResolver(VehicleFormSchema),
    mode: "onChange",
  });

  /* fn that handle all the states that needs to be reset when the dialog is closed */
  function closeAndReset() {
    reset();
    setDisplayError(false);
    closeDialog();
  }

  useEffect(() => {
    reset(VehicleFormDefaultValues);
  }, [VehicleFormDefaultValues, reset]);

  const onSubmit: SubmitHandler<VehicleFormState> = async (formValues) => {
    const partialDoc: VehicleFormState = {
      ...formValues,
      description:
        formValues.description === "" ? null : formValues.description,
      groupName: formValues.groupName === "" ? undefined : formValues.groupName,
    };

    if (vehicleDoc != null) {
      try {
        await handleEditVehicle(partialDoc);
        // Close dialog if successful.
        closeAndReset();
      } catch (error) {
        setDisplayError(true);
        devLogger.error(error);
      }
    } else {
      try {
        await handleSaveNewVehicle(partialDoc);
        // Close dialog if successful.
        closeAndReset();
      } catch (error) {
        setDisplayError(true);
        devLogger.error(error);
      }
    }
  };

  const vehicleHeader = (
    <div className="mb-4 flex w-full items-center justify-between rounded-t-lg bg-primary p-8 text-left text-white ">
      <HeadingOne fontColor="text-white" fontSize="text-xl">
        {vehicleDoc ? strings.EDIT_VEHICLE : strings.ADD_NEW_VEHICLE}
      </HeadingOne>
      <button type="button" onClick={() => closeAndReset()}>
        <XMarkIcon
          aria-label="close new requirement form"
          className="h-6 text-white"
        />
      </button>
    </div>
  );

  const errorMessage = vehicleDoc ? (
    <ErrorMessage
      message={strings.ERROR_EDITING_VEHICLE}
      clearMessage={() => setDisplayError(false)}
    />
  ) : (
    <ErrorMessage
      message={strings.ERROR_CREATING_VEHICLE}
      clearMessage={() => setDisplayError(false)}
    />
  );

  return (
    <BaseModal
      closeModal={closeAndReset}
      open={isDialogOpen}
      title={vehicleHeader}
      parentDivStyles="inline-block transform overflow-hidden  max-w-screen-lg rounded-lg bg-white text-left align-middle shadow-xl transition-all"
    >
      <div className="space-y-8 p-8 text-lg">
        <form
          className="space-y-8"
          autoComplete="off"
          onSubmit={handleSubmit(onSubmit)}
        >
          <div className="relative grid grid-cols-2 gap-4 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-2">
            {/* Field: Location */}
            <div>
              <Controller
                name="locationID"
                control={control}
                render={({ field }) => (
                  <BaseInputSelect
                    inputName="locationID"
                    text="Site Location"
                    admin={true}
                    required={true}
                    className="capitalize"
                    {...field}
                    value={field.value === null ? "" : field.value}
                    onChange={(event) =>
                      field.onChange(
                        event.target.value === "" ? null : event.target.value,
                      )
                    }
                  >
                    {/* The empty string for value tricks validation into failing */}
                    <option value="">None</option>
                    {siteKeyLocationList.map((siteLocation) => {
                      return (
                        <option key={siteLocation.id} value={siteLocation.id}>
                          {siteLocation.title}
                        </option>
                      );
                    })}
                  </BaseInputSelect>
                )}
              />
              {errors.locationID?.message && (
                <div className="mt-2 text-sm">
                  <StyledMessage type="error">
                    {{ message: errors.locationID.message }}
                  </StyledMessage>
                </div>
              )}
            </div>

            {/* Field: Title */}
            <div>
              <Controller
                name="title"
                control={control}
                render={({ field }) => (
                  <BaseInputText
                    text="Title"
                    inputName="title"
                    admin={true}
                    required={true}
                    {...field}
                  />
                )}
              />
              {errors.title?.message && (
                <div className="mt-2 text-sm">
                  <StyledMessage type="error">
                    {{ message: errors.title.message }}
                  </StyledMessage>
                </div>
              )}
            </div>

            {/* Field: Description */}
            <div>
              <Controller
                control={control}
                name="description"
                render={({ field }) => (
                  <BaseInputTextArea
                    id="description"
                    admin={true}
                    text="Description"
                    rows={3}
                    {...field}
                    value={
                      field.value === null
                        ? undefined
                        : field.value /* because value may be null */
                    }
                    onChange={(event) => {
                      // Store empty values as null instead of undefined
                      field.onChange(event.target.value ?? null);
                    }}
                    className="mt-1 flex rounded-md shadow-sm sm:mt-0"
                  />
                )}
              />
              {errors.description?.message && (
                <div className="mt-2 text-sm">
                  <StyledMessage type="error">
                    {{ message: errors.description.message }}
                  </StyledMessage>
                </div>
              )}
            </div>

            {/* Field: Group Name */}
            <div>
              <Controller
                control={control}
                name="groupName"
                render={({ field }) => (
                  <StyledTooltip
                    title={`Add a group name to help organize your vehicles. For example, you could group all of your service vehicles under a "Service" group and your sales vehicles under a "Sales" group. These can later be filtered on the dispatch board.`}
                  >
                    <BaseInputText
                      id="groupName"
                      admin={true}
                      text="Group Name"
                      {...field}
                      value={
                        field.value === null
                          ? undefined
                          : field.value /* because value may be null */
                      }
                      onChange={(event) => {
                        // Store empty values as null instead of undefined
                        field.onChange(event.target.value ?? null);
                      }}
                      className="mt-1 flex rounded-md shadow-sm sm:mt-0"
                    />
                  </StyledTooltip>
                )}
              />
              {errors.groupName?.message && (
                <div className="mt-2 text-sm">
                  <StyledMessage type="error">
                    {{ message: errors.groupName.message }}
                  </StyledMessage>
                </div>
              )}
            </div>

            {/* Field: Max Tasks */}
            <div>
              <Controller
                control={control}
                name="maxTasksPerDay"
                render={({ field }) => (
                  <BaseInputNumber
                    id="maxTasksPerDay"
                    admin={true}
                    text="Maximum Tasks Per Day"
                    {...field}
                    value={field.value /* because value may be null */}
                    onChange={(event) => {
                      const numberValue = event.target.valueAsNumber;
                      if (displayError === true && numberValue > 0) {
                        setDisplayError(false);
                      }
                      field.onChange(isNaN(numberValue) ? "" : numberValue);
                    }}
                    className="mt-1 flex rounded-md shadow-sm sm:mt-0"
                  />
                )}
              />
              {errors.maxTasksPerDay?.message && (
                <div className="mt-2 text-sm">
                  <StyledMessage type="error">
                    {{ message: errors.maxTasksPerDay.message }}
                  </StyledMessage>
                </div>
              )}
            </div>

            {/*/!* ASSIGNED TO *!/*/}
            {/*<div className="relative grid grid-cols-1 rounded border border-black p-6">*/}
            {/*  <label className="absolute -top-3 left-3 bg-white px-2 xs:col-span-2 sm:col-span-1 md:col-span-2">*/}
            {/*    {strings.ASSIGN_USER}{" "}*/}
            {/*  </label>*/}
            {/*  <div className="grid grid-cols-2">*/}
            {/*    {userList*/}
            {/*      .sort((a, b) => a.name.localeCompare(b.name))*/}
            {/*      .map((user) => (*/}
            {/*        <Controller*/}
            {/*          key={user.uid}*/}
            {/*          control={control}*/}
            {/*          name="defaultSiteKeyUserID"*/}
            {/*          render={({ field }) => {*/}
            {/*            const isChecked = field.value.includes(user.uid);*/}
            {/*            return (*/}
            {/*              <BaseInputCheckbox*/}
            {/*                label={user.name}*/}
            {/*                checked={isChecked}*/}
            {/*                onChange={(event) => {*/}
            {/*                  const newValue = event.target.checked*/}
            {/*                    ? [...field.value, user.uid]*/}
            {/*                    : field.value.filter((id) => id !== user.uid);*/}
            {/*                  field.onChange(newValue);*/}
            {/*                }}*/}
            {/*              />*/}
            {/*            );*/}
            {/*          }}*/}
            {/*        />*/}
            {/*      ))}*/}
            {/*  </div>*/}
            {/*  {errors.defaultSiteKeyUserID?.message && (*/}
            {/*    <div className="mt-2 text-sm">*/}
            {/*      <StyledMessage type="error">*/}
            {/*        {{ message: errors.defaultSiteKeyUserID.message }}*/}
            {/*      </StyledMessage>*/}
            {/*    </div>*/}
            {/*  )}*/}
            {/*</div>*/}

            {/* Working Hours */}
            <div className="col-span-2">
              <div className="relative grid grid-cols-1 rounded border border-black p-6">
                <label className="absolute -top-3 left-3 bg-white px-2">
                  Working Hours (Optional)
                </label>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Start Time
                    </label>
                    <div className="mt-1">
                      <Controller
                        control={control}
                        name="startTime"
                        render={({ field }) => (
                          <input
                            type="time"
                            id="startTime"
                            {...field}
                            value={field.value ?? ""}
                            onChange={(event) => {
                              field.onChange(event.target.value || undefined);
                            }}
                            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
                          />
                        )}
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      End Time
                    </label>
                    <div className="mt-1">
                      <Controller
                        control={control}
                        name="endTime"
                        render={({ field }) => (
                          <input
                            type="time"
                            id="endTime"
                            {...field}
                            value={field.value ?? ""}
                            onChange={(event) => {
                              field.onChange(event.target.value || undefined);
                            }}
                            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
                          />
                        )}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="relative grid grid-cols-1 rounded border border-black p-6 xs:grid-cols-2 sm:grid-cols-1 md:grid-cols-2 md:pl-2 md:pr-0 lg:col-span-2 lg:px-4">
              <label className="absolute -top-3 left-3 bg-white px-2 xs:col-span-2 sm:col-span-1 md:col-span-2">
                Work Types{" "}
              </label>
              <span className="contents sm:col-span-1">
                {validCraftTypes.map((craftType) => (
                  <Controller
                    key={craftType}
                    control={control}
                    name={`craftTypes`}
                    render={({ field }) => {
                      const isChecked = field.value.includes(craftType);
                      return (
                        <BaseInputCheckbox
                          label={getReadableCraftType(craftType)}
                          checked={isChecked}
                          onChange={(event) => {
                            const newValue = event.target.checked
                              ? [...field.value, craftType]
                              : field.value.filter((id) => id !== craftType);
                            field.onChange(newValue);
                          }}
                        />
                      );
                    }}
                  />
                ))}
              </span>
              {errors.craftTypes?.message && (
                <div className="mt-2 text-sm">
                  <StyledMessage type="error">
                    {{ message: errors.craftTypes.message }}
                  </StyledMessage>
                </div>
              )}
            </div>

            <div className="relative grid grid-cols-1 rounded border border-black p-6 xs:grid-cols-2 sm:grid-cols-1 md:grid-cols-2 md:pl-2 md:pr-0 lg:col-span-2 lg:px-4">
              <label className="absolute -top-3 left-3 bg-white px-2 xs:col-span-2 sm:col-span-1 md:col-span-2">
                Task Types{" "}
              </label>
              <span className="contents sm:col-span-1">
                {validTaskTypes.map((taskType) => (
                  <Controller
                    key={taskType}
                    control={control}
                    name={`taskTypes`}
                    render={({ field }) => {
                      const isChecked = field.value.includes(taskType);
                      return (
                        <BaseInputCheckbox
                          label={getReadableTaskType(taskType)}
                          checked={isChecked}
                          onChange={(event) => {
                            const newValue = event.target.checked
                              ? [...field.value, taskType]
                              : field.value.filter((id) => id !== taskType);
                            field.onChange(newValue);
                          }}
                        />
                      );
                    }}
                  />
                ))}
              </span>
              {errors.taskTypes?.message && (
                <div className="mt-2 text-sm">
                  <StyledMessage type="error">
                    {{ message: errors.taskTypes.message }}
                  </StyledMessage>
                </div>
              )}
            </div>

            {siteKeyDoc.customizations.showSmartSchedulingOptions === true && (
              <div className="col-span-2">
                <Controller
                  control={control}
                  name="omitFromOptimization"
                  render={({ field }) => (
                    <StyledTooltip title="When checked, this vehicle will be excluded from the smart scheduling optimization process">
                      <BaseInputCheckbox
                        label="Exclude from optimization"
                        checked={field.value || false}
                        onChange={(event) => {
                          field.onChange(event.target.checked);
                        }}
                      />
                    </StyledTooltip>
                  )}
                />
                {errors.omitFromOptimization?.message && (
                  <div className="mt-2 text-sm">
                    <StyledMessage type="error">
                      {{ message: errors.omitFromOptimization.message }}
                    </StyledMessage>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex w-full flex-col items-center justify-between gap-4 xs:flex-row">
            {userPermissions.permissions.isSiteAdmin && vehicleDoc?.id && (
              <ButtonColored
                className="w-full justify-center uppercase"
                kind="danger"
                type="button"
                onClick={() => handleDeleteVehicle(vehicleDoc.id)}
              >
                {strings.DELETE_VEHICLE}
              </ButtonColored>
            )}
            <BaseButtonSecondary
              type="button"
              className="w-full justify-center uppercase"
              onClick={closeAndReset}
            >
              {strings.buttons.CANCEL}
            </BaseButtonSecondary>

            <BaseButtonPrimary
              type="submit"
              formNoValidate
              disabled={isSubmitting}
              isBusy={isSubmitting}
              busyText={strings.buttons.BUSY_SAVING}
              className="w-full justify-center uppercase"
            >
              {strings.buttons.SAVE}
            </BaseButtonPrimary>
          </div>
        </form>
        {displayError ? (
          <span className="absolute bottom-10 left-1/2 w-3/4 -translate-x-1/2 sm:w-96">
            {errorMessage}
          </span>
        ) : null}
      </div>
    </BaseModal>
  );
}
